// TIP-614 — the block Stripe Embedded Checkout mounts into.
//
// Shared on purpose: the brief-purchase flow uses it now and TIP-615's
// membership step uses the same one, so the two cannot drift apart on the
// thing that is hardest to get right — the height.
//
// THE HEIGHT IS NEVER FIXED. Stripe renders wallet buttons where the browser
// supports them (Apple Pay in Safari, Google Pay in Chrome, neither in some),
// a card form, a returning customer's saved card, and validation errors — so
// the iframe's height differs per user and changes while they use it. A fixed
// height clips the confirm button for somebody. This container therefore sets
// no height at all and lets the iframe size itself; the panel above it caps
// the modal at 90vh and lets the column scroll.
//
// THERE IS NO PAY BUTTON HERE, and there must never be one. Stripe's confirm
// button lives inside the iframe. A second button of ours next to it is the
// fastest route to double-clicks and support tickets — the design says so
// twice, in the desktop and mobile notes.
import React from "react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { stripePromise } from "../../utils/stripe";

/**
 * @param clientSecret  from the backend's session-create call. Until it
 *                      arrives the skeleton shows; the provider is not
 *                      mounted with a null secret, which Stripe rejects.
 * @param loadingTitle  the card-shaped bridge's heading and lines. Passed in
 * @param loadingSub    so each flow can word its own wait.
 * @param loadingNote
 * @param footer        the "processed by Stripe" line under the block.
 * @param onComplete    fires INSTEAD of a redirect, when the session is
 *                      created with `redirect_on_completion: 'never'`. That is
 *                      the right shape for a modal: the customer never leaves
 *                      the page, so there is no return URL to get wrong and no
 *                      company to re-select. Harmless while the session still
 *                      carries a return_url — Stripe redirects and this never
 *                      fires — so it can be wired before the backend changes.
 */
const StripeCheckoutPanel = ({
  clientSecret,
  loadingTitle,
  loadingSub,
  loadingNote,
  footer,
  onComplete,
  testId = "stripe-checkout-panel",
}) => (
  <div className="cb-stripe-panel" data-testid={testId}>
    {clientSecret ? (
      // Keyed on the secret: a retry mints a NEW session, and Stripe's
      // provider does not swap secrets on an existing mount. Without the key
      // the customer would sit looking at the dead first session.
      <div className="cb-stripe-mount" data-testid={`${testId}-mount`}>
        <EmbeddedCheckoutProvider
          key={clientSecret}
          stripe={stripePromise}
          options={{ clientSecret, ...(onComplete ? { onComplete } : {}) }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    ) : (
      /* The shape of a card form, not a spinner over a blank panel: it says
         fields are about to appear rather than that something is stuck. */
      <div className="cb-stripe-bridge" role="status" data-testid={`${testId}-bridge`}>
        <p className="cb-stripe-bridge-title">{loadingTitle}</p>
        <p className="cb-stripe-bridge-sub">{loadingSub}</p>

        <div className="cb-stripe-skeleton" aria-hidden="true">
          <span className="cb-stripe-skeleton-label" />
          <span className="cb-stripe-skeleton-field" />
          <span className="cb-stripe-skeleton-label" />
          <span className="cb-stripe-skeleton-field" />
          <div className="cb-stripe-skeleton-row">
            <span className="cb-stripe-skeleton-field" />
            <span className="cb-stripe-skeleton-field" />
          </div>
        </div>

        {loadingNote && <p className="cb-stripe-bridge-note">{loadingNote}</p>}
      </div>
    )}

    {footer && <p className="cb-stripe-footer">{footer}</p>}
  </div>
);

export default StripeCheckoutPanel;
